import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlList } from 'app/main/pages/authentication/shared/url-list.enum';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface FeatureMetadata {
  id: number;
  languageCode: string;
  title: string;
  description: string;
  buttonLabel: string;
  showNotification: boolean;
  showUntil: string;
  redirectTo: string;
  configurationName: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureConfigService {
  constructor(private http: HttpClient) {}

  /**
   * Service for retrieving feature notification translation metadata from backend endpoints.
   *
   * Provides methods to fetch metadata for a specific feature, all active features, or the first active feature,
   * using language and configuration parameters. Uses organization-specific URL constants and Angular HttpClient.
   */
  getFeatureMetadata(featureKey: string, lang: string): Observable<FeatureMetadata> {
    const url = `${UrlList.FEATURE_NOTIFICATION_TRANSLATION}?languageCode=${lang}&configurationName=${featureKey}`;
    return this.http.get<FeatureMetadata>(url);
  }

  /**
   * Service for retrieving feature notification translation metadata from backend endpoints.
   *
   * Provides methods to fetch metadata for a specific feature, all active features, or the first active feature,
   * using language and configuration parameters. Utilizes organization-specific URL constants and Angular HttpClient.
   *
   * Methods:
   * - getFeatureMetadata: Fetches metadata for a given feature key and language.
   * - getAllActiveFeatureMetadata: Retrieves all active feature metadata for a given language.
   * - getFirstActiveFeatureMetadata: Gets the first active feature metadata for a given language, or null if none exist.
   */
  getAllActiveFeatureMetadata(lang: string): Observable<FeatureMetadata[]> {
    const url = `${UrlList.FEATURE_NOTIFICATION_TRANSLATION_ACTIVE}?languageCode=${lang}`;
    return this.http.get<FeatureMetadata[]>(url);
  }

  /**
   * Service for retrieving feature notification translation metadata from backend endpoints.
   *
   * Provides methods to fetch metadata for a specific feature, all active features, or the first active feature,
   * using language and configuration parameters. Utilizes organization-specific URL constants and Angular HttpClient.
   *
   * Methods:
   * - getFeatureMetadata: Fetches metadata for a given feature key and language.
   * - getAllActiveFeatureMetadata: Retrieves all active feature metadata for a given language.
   * - getFirstActiveFeatureMetadata: Gets the first active feature metadata for a given language, or null if none exist.
   */
  getFirstActiveFeatureMetadata(lang: string): Observable<FeatureMetadata | null> {
    return this.getAllActiveFeatureMetadata(lang).pipe(
      map(arr => (arr && arr.length > 0 ? arr[0] : null))
    );
  }
}
